import LocationMapPreview from '@app/src/components/Map/LocationMapPreview'
import type { CoordinateLocation } from '@app/src/types/resourceExplorer'
import { isValidCoordinate } from '@app/src/utils/coordinateParser'
import { Button, Stack, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

interface CoordinateAnswerDisplayProps {
  coordinates: CoordinateLocation[]
}

const CoordinateEntry: React.FC<{ location: CoordinateLocation }> = ({ location }) => {
  const { formatMessage } = useIntl()
  const [showMap, setShowMap] = useState(false)

  const hasValidCoordinates =
    location.latitude !== undefined &&
    location.longitude !== undefined &&
    isValidCoordinate(location.latitude, location.longitude)

  if (!hasValidCoordinates) {
    return null
  }

  return (
    <Stack spacing={1}>
      {location.description && <Typography variant="body2">{location.description}</Typography>}
      <Typography variant="body2">
        {location.latitude}, {location.longitude}
      </Typography>
      <Button
        size="small"
        variant="text"
        sx={{ alignSelf: 'flex-start', p: 0 }}
        onClick={() => setShowMap(prev => !prev)}
      >
        {showMap ? formatMessage({ id: 'general.hideMap' }) : formatMessage({ id: 'general.showOnMap' })}
      </Button>
      {showMap && (
        <LocationMapPreview latitude={location.latitude as number} longitude={location.longitude as number} />
      )}
    </Stack>
  )
}

const CoordinateAnswerDisplay: React.FC<CoordinateAnswerDisplayProps> = ({ coordinates }) => {
  const sortedCoordinates = [...coordinates].sort((a, b) => {
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER
    return orderA - orderB
  })

  return (
    <Stack spacing={2}>
      {sortedCoordinates.map((location, index) => (
        <CoordinateEntry key={location.id ?? index} location={location} />
      ))}
    </Stack>
  )
}

export default CoordinateAnswerDisplay
